import { useCallback, useMemo, useState } from 'react'
import type { Locale } from 'date-fns'
import useFavurTranslation from 'hooks/useFavurTranslation'
import useRolesViews from 'hooks/useRolesViews'
import usePerformEventToTask from 'pages/Tasks/hooks/usePerformEventToTask'
import { SNACKBAR_VARIANTS } from 'redesign/components/atoms/snackbar-base/constants'
import { useTimelineActionButtonContext } from 'redesign/services/timeline-action-button/use-timeline-action-button-context'
import { useToastContext } from 'redesign/services/toast/use-toast-context'
import type { AbsenceData, Task } from 'shared/graphql/graphql'
import { TASK_STATES, TASKS_STATUS } from '../ContentLists/TaskList/constants'
import { createTimeline } from '../timeline/factory/timeline-factory'
import getDeleteRequest from './utils/frontline/get-delete-request'

const useAbsenceRequestDetails = (task: Task, refetch: () => void) => {
  const [showForm, setShowForm] = useState(false)
  const { t, locale } = useFavurTranslation()
  const { personIds, isOffice } = useRolesViews()
  const { statusFe, status, favurUuid, taskData, person } = task
  const { setMessageDrawerProps, isBalanceDrawerOpen, setIsBalanceDrawerOpen } = useTimelineActionButtonContext()
  const { sendEvent } = usePerformEventToTask(favurUuid ?? '')
  const [showAbsencePlannerDrawer, setShowAbsencePlannerDrawer] = useState<boolean>(false)
  const { dateFrom, dateTo } = (taskData || {}) as AbsenceData
  const { openToast } = useToastContext()

  const timelineItems = createTimeline({
    task,
    t,
    locale: locale as Locale,
    personIds,
    isOffice,
  })

  const showConflicts = useMemo(() => statusFe !== TASK_STATES.closed, [statusFe])

  const showDeleteButton = useMemo(() => !isOffice && status === TASKS_STATUS.approvedOffice, [isOffice, status])

  const onDeletionRequestCompleted = useCallback(() => {
    refetch()
    openToast({ title: t('confirmation.requestSent'), variant: SNACKBAR_VARIANTS.success })
  }, [openToast, refetch, t])

  const onClickDeleteRequest = () =>
    getDeleteRequest({ setMessageDrawerProps, t, sendEvent, onDeletionRequestCompleted })

  return {
    isOffice,
    showDeleteButton,
    t,
    timelineItems,
    onClickDeleteRequest,
    showConflicts,
    showAbsencePlannerDrawer,
    person,
    setShowAbsencePlannerDrawer,
    isBalanceDrawerOpen,
    setIsBalanceDrawerOpen,
    showForm,
    setShowForm,
    dateFrom,
    dateTo,
  }
}

export default useAbsenceRequestDetails
